import { useCallback } from "react"
import { useSubscription } from "urql"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Circle } from "lucide-react"
import { MessageReceivedSubscription } from "@/lib/graphql/outbound"
import { TimeAgo } from "@/components/shared/time-ago"
import { StatusBadge } from "@/components/shared/status-badge"
import { useAuth } from "@/hooks/use-auth"
import type { ResultOf } from "@/lib/graphql/types"

type LiveMessage = NonNullable<ResultOf<typeof MessageReceivedSubscription>>["messageReceived"]

export function OutboundLiveFeed() {
  const { isConfigured } = useAuth()

  const handleSubscription = useCallback(
    (prev: LiveMessage[] | undefined, response: ResultOf<typeof MessageReceivedSubscription>) => {
      if (!response?.messageReceived) return prev ?? []
      const msg = response.messageReceived
      const current = prev ?? []
      if (current.some((m) => m.id === msg.id)) return current
      return [msg, ...current.slice(0, 49)]
    },
    [],
  )

  const [result] = useSubscription(
    { query: MessageReceivedSubscription, pause: !isConfigured },
    handleSubscription,
  )

  const messages = result.data ?? []

  return (
    <div className="rounded-lg glass-card flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
        <div className="flex items-center gap-2.5">
          <h3 className="text-sm font-semibold">Live Messages</h3>
          <div className="flex items-center gap-1.5">
            <Circle
              className={`h-2 w-2 fill-current ${
                isConfigured && !result.error ? "text-emerald-500 animate-pulse-dot" : "text-zinc-500"
              }`}
            />
            <span className="text-xs text-muted-foreground">
              {isConfigured && !result.error ? "streaming" : "waiting"}
            </span>
          </div>
        </div>
        <Link to="/messages" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
          View all
        </Link>
      </div>
      <div className="flex-1 py-2">
        <ScrollArea className="h-87.5">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Waiting for messages...
            </div>
          ) : (
            <div className="divide-y divide-border/30 px-3">
              {messages.map((msg) => (
                <Link
                  key={msg.id}
                  to={`/messages/${msg.id}`}
                  className="flex items-center justify-between px-2 py-3 rounded-md hover:bg-accent/50 transition-colors animate-slide-in-up"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="outline" className="text-2xs shrink-0 border-primary/30 text-primary">
                      {msg.eventType || "unknown"}
                    </Badge>
                    <StatusBadge status={msg.status} />
                  </div>
                  <TimeAgo date={msg.createdAt} />
                </Link>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  )
}
